import { useEffect, useRef, useState, lazy, Suspense } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../context/AuthContext';
import Login from '../pages/login/login';
import SignUpPage from '../pages/login/SignUpPage';
import { PrivateRoute } from './PrivateRoute';
import useMsal from '../auth/useMsal';
import useExternalMsal, { shouldRedirectToExternalSignup } from '../auth/useExternalMsal';
import PageHeader from '../components/page-header/PageHeader';
import SidePanelLeft from '../components/common/side-panel/SidePanelLeft';
import useSetData from '../app/useSetData';
import { useVesselStore } from '../context/VesselContext';
import SpinnerLoader from '../components/common/loader/SpinnerLoader';
import { useRotationStore } from '../context/RotationContext';
import { AppWrapper, PageWrapper, ContentWrapper } from '../App.styles';
import useGetRole from '../api/useGetRole';
import useGetVessels from '../api/useGetVessels';
import { useAppStore } from '../context/AppContext';
import { useAolStore } from '../context/AolContext';
import useGetAppsMenu from '../api/useGetAppsMenu';
import SidePanelPopup from '../components/common/side-panel-popup/SidePanelPopup';
import { SidePanelModulEnum } from '../enums/sidePanelModulEnum';
import queryParamsUtils from '../utils/queryParamsUtils';
import { AppFeatureEnum } from '../enums/appFeatureEnum';
import useGetAppFeatures from '../api/useGetAppFeatures';
import useAppFeature from '../app/useAppFeature';
import useApiLoaderHelper from '../utils/useApiLoaderHelper';
import LoadingPanel from '../components/common/loading-panel/LoadingPanel';
import { useSWService } from '../api/sw/useSWService';
import useGetUserSettings from '../api/useUserSettingsApi';
import { useEventStore } from '../context/EventContext';
import useUpdateMissingTasksIcon from '../app/useUpdateMissingTasksIcon';
import { useEventTypeStore } from '../context/EventTypeContext';
import { useTankCleaningStore } from '../context/TankCleaningContext';
import useGetPBIAccessToken from 'src/api/useGetPBIAccessToken';
import { QueryParamValueProps, useQueryParamsHandler } from 'odfjell-query-manager';
import { useCargoTempLogStore } from 'src/context/CargoTempLogContext';
import { useStowageStore } from 'src/context/StowageContext';
import { useViolationsStore } from 'src/context/ViolationsContext';
import { useStowageSelectionStore } from 'src/context/StowageSelectionContext';
import useCheckConnection from 'src/utils/connectivity/useCheckConnection';
import useBackgroundSyncRecovery from 'src/hooks/useBackgroundSyncRecovery';
import AppModalComponent from 'src/components/appModal/AppModal';
import { getAolGroupDate } from 'src/utils/aol/aolDateUtils';
import AiChat from '../components/ai-chat/AiChat';
import { prettifyDate } from 'odfjell-date-time-utils/dateTimeFormatter';
import { hasPermission } from 'src/utils/stowage/permissions/stowagePermissionsUtils';
import { stowageFeatureEnum } from 'src/enums/stowage/stowageFeatureEnum';
import { isShipUserRole } from '../utils/verification/verificationUtils';
import Unauthorized from '../pages/unauthorized/Unauthorized';
import { useBunkerContext } from 'src/context/BunkerContext';
import { useVerificationStore } from 'src/context/VerificationContext';
import usePreloadBaseStowageData from 'src/hooks/stowage/usePreloadBaseStowageData';
import usePreloadVesselData from 'src/hooks/usePreloadVesselData';
import { useAppStatus } from '../context/AppStatusContext';
import { RoleTypeEnum } from '../enums/roleTypeEnum';
import MaintenanceBanner from '../components/common/maintenance-banner/MaintenanceBanner';
import StatusMessageBanner from '../components/common/status-message-banner/StatusMessageBanner';
import { useUtcCountdown } from 'src/hooks/useUtcCountdown';

const EVENTS_CONSUMABLES_PATHS = ['/report-events', '/tank-cleaning'];
const STOWAGE_PATHS = ['/stowage'];
const getVoyageGroup = (path: string): 'events-consumables' | 'stowage' | null => {
    if (EVENTS_CONSUMABLES_PATHS.some((p) => path.startsWith(p))) return 'events-consumables';
    if (STOWAGE_PATHS.some((p) => path.startsWith(p))) return 'stowage';
    return null;
};

// ============================================
// LAZY-LOADED PAGE COMPONENTS (Performance Optimization)
// ============================================
// These pages are loaded on-demand when the route is accessed
// This significantly reduces the initial bundle size and load time
const ReportEventsComponent = lazy(() => import('../pages/report-events/ReportEvents'));
const SelectVesselPage = lazy(() => import('../pages/select-vessel/SelectVessel'));
const TankCleaningComponent = lazy(() => import('../pages/tank-cleaning/TankCleaning'));
const CargoTempLogComponent = lazy(() => import('../pages/cargo-temp-log/cargoTempLog'));
const VerificationComponent = lazy(() => import('../pages/verification/Verification'));
const PBIReportWrapper = lazy(() => import('../pages/pbi-report/PBIReportWrapper'));
const AolComponent = lazy(() => import('src/pages/aol/aol'));
const StowagePage = lazy(() => import('src/pages/stowage/StowagePage'));
const BunkeringPage = lazy(() => import('src/pages/bunkering/BunkeringPage'));
const AdminPage = lazy(() => import('src/pages/admin/AdminPage'));
const MaintenancePage = lazy(() => import('src/pages/maintenance/MaintenancePage'));

const AdminMaintenanceBanner: React.FC<{ maintenanceMode: any; refetch: () => void }> = ({ maintenanceMode, refetch }) => {
    const startsIn = useUtcCountdown(maintenanceMode?.scheduledStartTimeUtc);
    const remaining = useUtcCountdown(maintenanceMode?.estimatedEndTimeUtc);

    useEffect(() => {
        if (startsIn === 0) {
            refetch();
        }
    }, [startsIn, refetch]);

    const label = maintenanceMode?.isMaintenanceActive
        ? remaining != null
            ? `Maintenance mode is ACTIVE — you have admin bypass (~${remaining > 0 ? `${remaining} min remaining` : 'ending any moment'})`
            : 'Maintenance mode is ACTIVE — you have admin bypass'
        : `Maintenance scheduled in ${startsIn ?? '?'} minutes`;
    return (
        <div
            style={{
                background: '#d4edda',
                borderBottom: '1px solid #28a745',
                padding: '6px 16px',
                textAlign: 'center',
                fontSize: '13px',
                color: '#155724',
            }}
        >
            {label}
        </div>
    );
};

const MaintenanceGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { maintenanceMode, refetch } = useAppStatus();
    const { role } = useAuthStore();
    const isAdmin = role?.roleType === RoleTypeEnum.Admin;
    const isMaintenanceActive = maintenanceMode?.isMaintenanceActive === true && !isAdmin;
    const isMaintenanceScheduled = maintenanceMode?.isMaintenanceScheduled === true && !isAdmin;
    const showAdminIndicator = (maintenanceMode?.isMaintenanceActive || maintenanceMode?.isMaintenanceScheduled) && isAdmin;

    if (isMaintenanceActive) {
        return (
            <Suspense fallback={null}>
                <MaintenancePage />
            </Suspense>
        );
    }

    return (
        <>
            {isMaintenanceScheduled && <MaintenanceBanner />}
            {showAdminIndicator && <AdminMaintenanceBanner maintenanceMode={maintenanceMode} refetch={refetch} />}
            <StatusMessageBanner />
            {children}
        </>
    );
};

const RouterContent = () => {
    const { checkAuthStatus, initHandleRedirect, handleAppAccessToken, isInitialized } = useMsal();
    const { checkExternalAuthStatus, hasExternalAccountSession, isExternalMsalInitialized } = useExternalMsal();
    const { loggedInUser, isAuthenticating, isVerifyingToken, setIsVerifyingToken, role } = useAuthStore();
    const [, setIsAuthorized] = useState<boolean | undefined>(undefined);
    const location = useLocation();
    const { getRole } = useGetRole();
    const { getAppFeatures } = useGetAppFeatures();
    const { getVessels } = useGetVessels();
    const { getAppsMenuHtml } = useGetAppsMenu();
    const { isFeatureEnabled } = useAppFeature();
    const {
        selectedVessel,
        setSelectedVessel,
        selectedListVessel,
        setSelectedListVessel,
        isVesselListRetrieved,
        vesselList,
        filteredVesselList,
        includeInactiveVessels,
    } = useVesselStore();
    const activeVessel = selectedVessel ?? selectedListVessel;
    const { visibleSidePanelModuleIndex, setVisibleSidePanelModuleIndex, appModalData, appFeatureSettings } = useAppStore();
    const { setRotation } = useRotationStore();
    const { startPreload: startStowageBaseDataPreload, resetPreload: resetStowageBaseDataPreload } = usePreloadBaseStowageData();
    const { startPreload: startVesselPreload, applyPreloadedDataIfMatch, resetPreload: resetVesselPreload } = usePreloadVesselData();
    const { hasAuthorizedAppToken } = queryParamsUtils();
    const { startCheckLatency } = useCheckConnection();
    useBackgroundSyncRecovery();
    const navigate = useNavigate();
    const { getMutatingLoaderMessage } = useApiLoaderHelper();
    const { setupServiceWorker } = useSWService();
    const { getUserSettings } = useGetUserSettings();
    const { updateAllMissingTasks } = useUpdateMissingTasksIcon();
    const { setSelectedEvent, setFullEventArray, setDeltaEventArray, setEventDataForVoyageList } = useEventStore();
    const { setSelectedEventType } = useEventTypeStore();
    const { getPbiAccessToken } = useGetPBIAccessToken();
    const {
        setUpdatedRotation,
        setCurrentPortCall,
        setNextPortCall,
        setIsRotationFilterActive,
        setIsFilterPanelOpen,
        setSelectedNode,
        setSelectedStowageVoyages,
        selectedStowageRotationPortCall,
    } = useRotationStore();
    const { triggerQueryParamsUpdate } = useQueryParamsHandler();
    const previousPathRef = useRef<string | null>(null);
    const {
        setSelectedTankCleaningPortCall,
        setSelectedChemicalRobForPortCall,
        setSelectedChemicalOrderForPortCall,
        setSelectedChemicalOrderForPortCallList,
        setChemicalOrderUpdateReportData,
        setChemicalRobForPortCallList,
        setChemicalOrderForPortCallList,
    } = useTankCleaningStore();
    const { setCargoTempLogDates, setCargoTemperatureConfigs, setFilteredCargoTemperatureConfigs } = useCargoTempLogStore();
    const {
        aolGroupStatusMessage,
        setSelectedAolGroup,
        selectedAolGroup,
        setSelectedAolDataItem,
        setAolGroups,
        setAolDataItems,
        setAolItems,
        setNoonReports,
        setAolEventsArray,
        setAolEventsVesselImo,
        setAolEventsLoadedRange,
    } = useAolStore();
    const {
        setMissingCargoTempLogStatuses,
        setMissingRobStatuses,
        setMissingNoonReportStatuses,
        setNumMissingBunkerRegistrations,
        setNumMissingBunkerRob,
        setIsVoyageSelectionPanelOpen,
    } = useAppStore();
    const {
        setStowagePlanRotationPortCallList,
        setStowagePlanRotationCargoList,
        setSelectedStowageRotationBerthCall,
        setSelectedStowageRotationPortCall,
        setImosRotationPortCallList,
        setImosRotationCargoList,
        setStowageRotationTree,
    } = useRotationStore();
    const {
        setTankPlan,
        setStowagePlans,
        setActiveStowagePlan,
        setIsStowagePlanUpdated,
        setStowageModalData,
        setLoadedMasterStowagePlan,
        setImosSyncChanges,
        setMasterPlanSyncData,
        setStowagePlanChanges,
        clearPinnedPanels,
    } = useStowageStore();
    const { setSelectedCargoOperation } = useStowageSelectionStore();
    const { setViolations } = useViolationsStore();
    const { setBunkerings, setBunkeringEvents, setSelectedBunkering, setSelectedBunkeringEvent, selectedBunkeringDate, setFuelRobData } = useBunkerContext();
    const {
        setReportMetadataList,
        setPopulatedReportMetadataList,
        setVerificationFilter,
        setSelectedReportMetadataList,
        setFilteredReportMetadataList,
        setSelectedReportMetadata,
        setReportComment,
    } = useVerificationStore();

    useSetData();

    const { queryParamUpdate, triggerAllQueryParamsFromUrl, getQueryParamValueFromUrl } = useQueryParamsHandler();
    // ============= USE EFFECTS ================
    const shouldUseExternalSignup = shouldRedirectToExternalSignup();

    useEffect(() => {
        triggerAllQueryParamsFromUrl();
        resetContextOnLocationChange();
    }, [location]);

    useEffect(() => {
        if (loggedInUser && queryParamUpdate && isVesselListRetrieved) {
            // Skip vessel selection for pages that work across all vessels (no specific vessel required)
            //const pathsWithoutVesselRequirement = ['/select-vessel', '/verification'];
            const pathsWithoutVesselRequirement = ['/select-vessel'];

            if (!pathsWithoutVesselRequirement.includes(location.pathname)) {
                selectShipByImoNumber(loggedInUser.appAccessToken);
            }
        }
    }, [queryParamUpdate, loggedInUser, isVesselListRetrieved]);
    useEffect(() => {
        if (loggedInUser) {
            setIsAuthorized(true);
            // All these calls run in parallel - they trigger async operations.
            // Each call is isolated in its own try/catch: if one throws synchronously
            // (e.g. before its first internal await), it must not prevent the others
            // from firing. Without this isolation, a single synchronous error here
            // silently aborts the entire effect and NONE of the API calls fire -
            // this was the root cause of "no API calls until page refresh" after
            // signing in via the CIAM redirect flow.
            const safeCall = (name: string, fn: () => void) => {
                try {
                    fn();
                } catch (error) {
                    console.error(`[RouterContent] loggedInUser effect: ${name}() threw synchronously`, error);
                }
            };
            safeCall('getRole', getRole);
            safeCall('getUserSettings', getUserSettings);
            safeCall('getVessels', getVessels);
            safeCall('getAppFeatures', getAppFeatures);
            safeCall('getAppsMenuHtml', getAppsMenuHtml);
            safeCall('getPbiAccessToken', getPbiAccessToken);
            // OPTIMIZATION: Start preloading stowage base data (GetProducts, GetBerthNames,
            // GetCoatings, etc.) in parallel with the above calls instead of waiting for
            // vessel selection. This saves ~500-800ms on page load.
            safeCall('startStowageBaseDataPreload', startStowageBaseDataPreload);
            // OPTIMIZATION: Start preloading vessel-specific data (GetVessel) using IMO from
            // localStorage or URL params. This runs in parallel with GetVessels instead of
            // waiting for the vessel list to be fetched first.
            safeCall('startVesselPreload', startVesselPreload);
        }
    }, [loggedInUser]);
    useEffect(() => {
        setupServiceWorker();
        // Only call MSAL functions after initialization is complete
        if (loggedInUser) {
            setIsVerifyingToken(false);
        } else if (location.pathname !== '/signup' && !hasAuthorizedAppToken() && !isExternalMsalInitialized) {
            // The external MSAL PublicClientApplication initializes asynchronously
            // (see ensureInitialized in useExternalMsal). Calling getAllAccounts() via
            // hasExternalAccountSession() before that promise resolves can return an
            // empty array even though a valid cached account exists, which would
            // otherwise fall through to the shouldUseExternalSignup branch below and
            // permanently skip re-verification for this mount (since this effect only
            // re-runs on dependency changes, not on a timer). Wait here instead - this
            // effect re-runs automatically once isExternalMsalInitialized flips to true.
        } else if (!hasAuthorizedAppToken() && location.pathname !== '/signup' && hasExternalAccountSession()) {
            // Always re-verify against a live external account session before trusting the
            // sessionStorage "pending_approval" flag - it is only ever cleared on a successful
            // sign-in (see completeApprovedExternalSignIn), so a stale flag from an earlier
            // not-yet-approved attempt must not permanently short-circuit later checks once the
            // user has since been approved and returns with a valid cached/renewed account.
            checkExternalAuthStatus();
        } else if (shouldUseExternalSignup) {
            setIsVerifyingToken(false);
        } else if (isInitialized && location.pathname !== '/signup') {
            if (!hasAuthorizedAppToken()) {
                // Complete a pending MSAL redirect response (e.g. returning from the
                // Microsoft login window after clicking "Odfjell employee" on /login) -
                // initHandleRedirect only completes sign-in if a redirect result is
                // actually present, it never triggers a new login itself.
                initHandleRedirect();
                // checkAuthStatus() is a silent status check - it no longer force-
                // redirects to Microsoft when no account is found, so it's safe on
                // every route here, including /login.
                checkAuthStatus();
            } else {
                handleAppAccessToken();
            }
        } else if (location.pathname === '/signup') {
            // /signup uses a separate external MSAL instance (see useExternalMsal /
            // SignUpPage) and never goes through the internal checkAuthStatus() flow
            // above, so isVerifyingToken would otherwise stay stuck at its default
            // `true` forever, hiding the entire <Routes> tree (including SignUpPage)
            // behind a blank page. Resolve it immediately so the route can render.
            setIsVerifyingToken(false);
        }
    }, [checkAuthStatus, checkExternalAuthStatus, handleAppAccessToken, hasAuthorizedAppToken, hasExternalAccountSession, initHandleRedirect, isExternalMsalInitialized, isInitialized, location.pathname, loggedInUser, setIsVerifyingToken, setupServiceWorker, shouldUseExternalSignup]);

    useEffect(() => {
        const path = location.pathname;

        const getPageName = (): string => {
            if (path.startsWith('/report-events')) return 'Events';
            if (path.startsWith('/cargo-temp-log')) return 'Cargo Temp';
            if (path.startsWith('/tank-cleaning')) return 'Consumables';
            if (path.startsWith('/dashboards')) return 'Dashboards';
            if (path.startsWith('/bunkering')) return 'Bunker';
            if (path.startsWith('/stowage')) return 'Stowage';
            if (path.startsWith('/aol')) return 'AOL';
            return '';
        };

        const isShipUser = role ? isShipUserRole(role) : false;

        if (path.startsWith('/verification')) {
            document.title = 'OW Verification';
        } else if (path.startsWith('/admin')) {
            document.title = 'OW Admin';
        } else if (path.startsWith('/login')) {
            document.title = 'OW Login';
        } else if (path.startsWith('/select-vessel')) {
            document.title = 'OW Select Vessel';
        } else if (path.startsWith('/unauthorized')) {
            document.title = 'OW Unauthorized';
        } else if (activeVessel) {
            const pageName = getPageName();
            const suffix =
                (path.startsWith('/stowage') && selectedStowageRotationPortCall ? ' ' + selectedStowageRotationPortCall.voyageNumber : '') +
                (path.startsWith('/aol') && selectedAolGroup ? ' ' + prettifyDate(getAolGroupDate(selectedAolGroup)) : '') +
                (path.startsWith('/bunkering') && selectedBunkeringDate ? ' ' + prettifyDate(selectedBunkeringDate) : '');
            const vesselPrefix = isShipUser ? '' : activeVessel.vesselName + ' ';
            document.title = `OW ${vesselPrefix}${pageName}${suffix}`.trimEnd();
        } else {
            document.title = 'OW';
        }
    }, [location, activeVessel, selectedStowageRotationPortCall, selectedAolGroup, role, selectedBunkeringDate]);

    const mutatingLoaderMessage = getMutatingLoaderMessage();

    const setLocalStorage = (imoNumber: number) => {
        localStorage.setItem('selectedShipImoNumber', JSON.stringify(imoNumber));
    };
    const selectShipByImoNumber = (appAccessToken?: string) => {
        const url_imoNumber = getQueryParamValueFromUrl('imoNumber');
        const ls_imoNumber = localStorage.getItem('selectedShipImoNumber');

        if (url_imoNumber && selectedListVessel && +url_imoNumber === selectedListVessel.imoNumber) {
            return;
        }

        if (!url_imoNumber && !ls_imoNumber && !selectedVessel) {
            // Auto-select first active vessel instead of redirecting to /select-vessel
            const activeVessels = filteredVesselList.length > 0 ? filteredVesselList : vesselList;
            if (activeVessels.length > 0) {
                const firstVessel = activeVessels[0];
                const queryParamValues: QueryParamValueProps[] = [{ name: 'imoNumber', newValue: firstVessel.imoNumber.toString() }];
                triggerQueryParamsUpdate(queryParamValues, 'clear-all');
            }
            return;
        }

        if (!url_imoNumber && ls_imoNumber) {
            // Validate that the stored vessel is still active (unless inactive vessels are included)
            const storedVessel = vesselList.find((v) => v.imoNumber.toString() === ls_imoNumber);
            if (storedVessel && (storedVessel.isActive || includeInactiveVessels)) {
                const queryParamValues: QueryParamValueProps[] = [{ name: 'imoNumber', newValue: ls_imoNumber.toString() }];
                triggerQueryParamsUpdate(queryParamValues, 'clear-all');
                return;
            }
            // Stored vessel is inactive — clear it and fall through to auto-select first active vessel
            localStorage.removeItem('selectedShipImoNumber');
            const activeVessels = filteredVesselList.length > 0 ? filteredVesselList : vesselList.filter((v) => v.isActive);
            if (activeVessels.length > 0) {
                const firstVessel = activeVessels[0];
                const queryParamValues: QueryParamValueProps[] = [{ name: 'imoNumber', newValue: firstVessel.imoNumber.toString() }];
                triggerQueryParamsUpdate(queryParamValues, 'clear-all');
            }
            return;
        }

        if (appAccessToken) {
            const queryParamValues: QueryParamValueProps[] = [{ name: 'appAccessToken', newValue: appAccessToken }];
            triggerQueryParamsUpdate(queryParamValues);
        }

        if (url_imoNumber) {
            const vessel = vesselList.find((v) => v.imoNumber.toString() === url_imoNumber);
            if (vessel) {
                setSelectedListVessel(vessel);
                setLocalStorage(+url_imoNumber);
                resetContext();

                startCheckLatency(vessel.imoNumber);

                if (isFeatureEnabled({ featureEnum: AppFeatureEnum.Notifications, date: new Date() })) {
                    setTimeout(() => {
                        const currentLsImo = localStorage.getItem('selectedShipImoNumber');
                        if (currentLsImo && +currentLsImo === vessel.imoNumber) {
                            updateAllMissingTasks(vessel.imoNumber);
                        }
                    }, 2000);
                }
            }
        }

        return;
    };

    const resetContextOnLocationChange = () => {
        const prevPath = previousPathRef.current;
        const currPath = location.pathname;
        previousPathRef.current = currPath;

        const prevGroup = prevPath ? getVoyageGroup(prevPath) : null;
        const currGroup = getVoyageGroup(currPath);

        // Within same voyage-selector group (Events ↔ Consumables, or Stowage → Stowage): preserve voyage selection
        if (prevGroup && prevGroup === currGroup) {
            setIsVoyageSelectionPanelOpen(null);
            return;
        }

        // Leaving any voyage-selector group (or switching between groups): clear voyageNumbers from URL
        if (prevGroup && prevGroup !== currGroup) {
            triggerQueryParamsUpdate([{ name: 'voyageNumbers', clearParam: true }]);
        }

        setSelectedStowageVoyages(null);
        setIsVoyageSelectionPanelOpen(null);
    };

    const resetContext = () => {
        // Close side panel popup (Settings/About/AppsMenu)
        setVisibleSidePanelModuleIndex(SidePanelModulEnum.NONE);

        setRotation(null);
        setFullEventArray(null);
        setAolEventsArray(null);
        setAolEventsVesselImo(null);
        setAolEventsLoadedRange(null);
        setDeltaEventArray(null);
        setSelectedNode(null);
        setCurrentPortCall(null);
        setNextPortCall(null);
        setSelectedStowageVoyages(null);
        setIsRotationFilterActive(false);
        setIsFilterPanelOpen(false);
        setUpdatedRotation(null);
        setSelectedEvent(null);
        setSelectedEventType(null);
        setEventDataForVoyageList(null);

        // CONSUMABLES
        setSelectedTankCleaningPortCall(null);
        setSelectedChemicalRobForPortCall(null);
        setChemicalRobForPortCallList(null);
        setChemicalOrderForPortCallList(null);
        setSelectedChemicalOrderForPortCall(null);
        setSelectedChemicalOrderForPortCallList(null);
        setChemicalOrderUpdateReportData(null);

        //CARGO TEMPERATURE LOG
        setNoonReports(null);
        setCargoTempLogDates(null);
        setCargoTemperatureConfigs(null);
        setFilteredCargoTemperatureConfigs([]);

        setMissingNoonReportStatuses(null);
        setMissingRobStatuses(null);
        setMissingCargoTempLogStatuses(null);
        setNumMissingBunkerRegistrations(0);
        setNumMissingBunkerRob(0);

        //AOL
        setSelectedAolDataItem(null);
        setSelectedAolGroup(null);
        setAolItems(null);
        setAolDataItems([]);
        setAolGroups([]);

        //STOWAGE
        setLoadedMasterStowagePlan(null);
        setStowagePlanRotationPortCallList(null);
        setStowagePlanRotationCargoList(null);
        setTankPlan(null);
        setStowagePlans(null);
        setActiveStowagePlan(null);
        setViolations(null);
        setSelectedStowageRotationPortCall(null);
        setSelectedStowageRotationBerthCall(null);
        setSelectedCargoOperation(null);
        setImosRotationPortCallList(null);
        setImosRotationCargoList(null);
        setIsStowagePlanUpdated(null);
        setStowageModalData(null);
        setStowageRotationTree(null);
        setStowagePlanChanges([]);
        setImosSyncChanges([]);
        setMasterPlanSyncData(null);
        clearPinnedPanels();

        // BUNKERING
        setSelectedBunkering(null);
        setSelectedBunkeringEvent(null);
        setBunkerings([]);
        setBunkeringEvents([]);
        setFuelRobData([]);

        // VERIFICATION
        setReportMetadataList(null);
        setPopulatedReportMetadataList(null);
        setFilteredReportMetadataList(null);
        setSelectedReportMetadataList(null);
        setVerificationFilter(null);
        setSelectedReportMetadata(null);
        setReportComment(null);
    };

    const RootRoute = () => {
        const windowParams = new URLSearchParams(window.location.search);
        const pathname = shouldUseExternalSignup ? '/signup' : '/login';
        return <Route path="/" element={<Navigate to={{ pathname, search: `?${windowParams}` }} />} />;
    };

    const LoginRoute = () => {
        const windowParams = new URLSearchParams(window.location.search);
        if (hasAuthorizedAppToken() && loggedInUser) {
            return <Route path="/login" element={<Navigate to={{ pathname: '/report-events', search: `?${windowParams}` }} />} />;
        } else if (loggedInUser) {
            return <Route path="/login" element={<Navigate to={{ pathname: '/report-events', search: `?${windowParams}` }} />} />;
        } else if (shouldUseExternalSignup) {
            return <Route path="/login" element={<Navigate to={{ pathname: '/signup', search: `?${windowParams}` }} />} />;
        }
        return <Route path="/login" element={<Login />} />;
    };

    const SignUpRoute = () => {
        return <Route path="/signup" element={<SignUpPage />} />;
    };

    const SelectShipRoute = () => {
        if (activeVessel) {
            const windowParams = new URLSearchParams(window.location.search);
            windowParams.set('imoNumber', activeVessel.imoNumber.toString());
            return (
                <Route
                    path="/select-vessel"
                    element={
                        <PrivateRoute>
                            <Navigate to={{ pathname: 'report-events', search: `?${windowParams}` }} />
                        </PrivateRoute>
                    }
                />
            );
        }
        return (
            <Route
                path="/select-vessel"
                element={
                    <PrivateRoute>
                        <SelectVesselPage />
                    </PrivateRoute>
                }
            />
        );
    };

    const EnableCargoConsumablesRoute = () => {
        // Always register the route to prevent "No routes matched" errors during vessel loading
        const isEnabled =
            activeVessel && !activeVessel.hasOrcaWebSimpleMode && isFeatureEnabled({ featureEnum: AppFeatureEnum.CargoConsumable, date: new Date() });

        return (
            <Route
                path="/tank-cleaning"
                element={
                    isEnabled ? (
                        <PrivateRoute>
                            <TankCleaningComponent />
                        </PrivateRoute>
                    ) : activeVessel ? (
                        <Navigate to={{ pathname: '/', search: window.location.search }} replace />
                    ) : null
                }
            />
        );
    };

    const EnableVerificationRoute = () => {
        // Always register the route to prevent "No routes matched" errors during role/vessel loading
        const isEnabled =
            role &&
            isFeatureEnabled({ featureEnum: AppFeatureEnum.Verification, date: new Date(), role: role.roleType }) &&
            !(isShipUserRole(role) && (!activeVessel || activeVessel.hasOrcaWebSimpleMode));

        // Only redirect once all required data is loaded:
        // - appFeatureSettings must be populated (so isFeatureEnabled can evaluate correctly)
        // - For ship users, activeVessel must be set (so simple-mode check is valid)
        // Without this guard, role loading before vessel/features causes a premature redirect
        // to / → /login → /report-events for direct /verification URL navigation.
        const isReadyToRedirect = role && appFeatureSettings.length > 0 && (!isShipUserRole(role) || !!activeVessel);

        return (
            <Route
                path="/verification"
                element={
                    isEnabled ? (
                        <PrivateRoute>
                            <VerificationComponent />
                        </PrivateRoute>
                    ) : isReadyToRedirect ? (
                        <Navigate to={{ pathname: '/', search: window.location.search }} replace />
                    ) : null
                }
            />
        );
    };

    const CargoTempLogRoute = () => {
        // Always register the route to prevent "No routes matched" errors during vessel loading
        const isEnabled =
            activeVessel &&
            !activeVessel.hasOrcaWebSimpleMode &&
            isFeatureEnabled({ featureEnum: AppFeatureEnum.CargoTemperatureControlLog, date: new Date() });

        return (
            <Route
                path="/cargo-temp-log"
                element={
                    isEnabled ? (
                        <PrivateRoute>
                            <CargoTempLogComponent />
                        </PrivateRoute>
                    ) : activeVessel ? (
                        <Navigate to={{ pathname: '/', search: window.location.search }} replace />
                    ) : null
                }
            />
        );
    };

    const PBIReportRoute = () => {
        // Always register the route to prevent "No routes matched" errors
        const isEnabled = isFeatureEnabled({ featureEnum: AppFeatureEnum.PbiReports, date: new Date() });

        return (
            <Route
                path="/dashboards"
                element={
                    isEnabled ? (
                        <PrivateRoute>
                            <PBIReportWrapper />
                        </PrivateRoute>
                    ) : appFeatureSettings.length > 0 ? (
                        <Navigate to={{ pathname: '/', search: window.location.search }} replace />
                    ) : null
                }
            />
        );
    };

    const StowageRoute = () => {
        const isRoleResolved = role?.roleType !== RoleTypeEnum.Unknown;
        const hasStowageViewPermission = isRoleResolved && !!role && hasPermission(role, stowageFeatureEnum.ViewStowagePlan);

        // Always register the route to prevent "No routes matched" errors during vessel/role loading
        const isEnabled =
            isFeatureEnabled({ featureEnum: AppFeatureEnum.Stowage, date: new Date(), imoNumber: activeVessel?.imoNumber }) &&
            hasStowageViewPermission;

        // Only redirect once role/app features are resolved. Users without stowage permission
        // (including external users) must be redirected even when no vessel is selected to
        // prevent direct URL access from rendering the stowage page.
        const isReadyToRedirect =
            isRoleResolved &&
            appFeatureSettings.length > 0 &&
            (!hasStowageViewPermission || !!selectedVessel);

        return (
            <Route
                path="/stowage"
                element={
                    isEnabled ? (
                        <PrivateRoute>
                            <StowagePage />
                        </PrivateRoute>
                    ) : isReadyToRedirect ? (
                        <Navigate to={{ pathname: '/', search: window.location.search }} replace />
                    ) : null
                }
            />
        );
    };
    const BunkeringRoute = () => {
        // Always register the route to prevent "No routes matched" errors during vessel loading
        const isEnabled = isFeatureEnabled({ featureEnum: AppFeatureEnum.Bunkering, date: new Date(), imoNumber: activeVessel?.imoNumber });

        return (
            <Route
                path="/bunkering"
                element={
                    isEnabled ? (
                        <PrivateRoute>
                            <BunkeringPage />
                        </PrivateRoute>
                    ) : selectedVessel && appFeatureSettings.length > 0 ? (
                        <Navigate to={{ pathname: '/', search: window.location.search }} replace />
                    ) : null
                }
            />
        );
    };

    const UnauthorizedRoute = () => {
        if (role) {
            return <Route path="/unauthorized" element={<Navigate to={{ pathname: '/' }} />} />;
        }
        return <Route path="/unauthorized" element={<Unauthorized />} />;
    };
    return (
        <AppWrapper>
            <AppModalComponent />
            {(mutatingLoaderMessage || aolGroupStatusMessage) && !appModalData && (
                <SpinnerLoader showOverlay={true} message={mutatingLoaderMessage ?? aolGroupStatusMessage ?? ''} />
            )}
            <PageHeader />
            <MaintenanceGuard>
                <PageWrapper>
                    {!isVerifyingToken &&
                        !isAuthenticating &&
                        ((activeVessel && activeVessel.imoNumber) || location.pathname === '/verification') && <SidePanelLeft />}
                    <ContentWrapper>
                        <LoadingPanel />
                        {!isVerifyingToken && !isAuthenticating && (
                            <Suspense
                                fallback={
                                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', width: '100%' }}>
                                        <div
                                            style={{
                                                width: '32px',
                                                height: '32px',
                                                border: '3px solid rgba(0, 90, 140, 0.2)',
                                                borderTopColor: '#005a8c',
                                                borderRadius: '50%',
                                                animation: 'spin 0.8s linear infinite',
                                            }}
                                        />
                                    </div>
                                }
                            >
                                <Routes>
                                    {RootRoute()}
                                    {LoginRoute()}
                                    {SignUpRoute()}
                                    {SelectShipRoute()}
                                    {UnauthorizedRoute()}
                                    <Route
                                        path="/report-events"
                                        element={
                                            <PrivateRoute>
                                                <ReportEventsComponent />
                                            </PrivateRoute>
                                        }
                                    />
                                    <Route
                                        path="/aol"
                                        element={
                                            <PrivateRoute>
                                                <AolComponent />
                                            </PrivateRoute>
                                        }
                                    />
                                    {CargoTempLogRoute()}
                                    {EnableCargoConsumablesRoute()}
                                    {EnableVerificationRoute()}
                                    {PBIReportRoute()}
                                    {StowageRoute()}
                                    {BunkeringRoute()}
                                    <Route path="/maintenance" element={<MaintenancePage />} />
                                    <Route
                                        path="/admin"
                                        element={
                                            <PrivateRoute>
                                                {isFeatureEnabled({ featureEnum: AppFeatureEnum.AdminPage, date: new Date() }) ? (
                                                    <AdminPage />
                                                ) : appFeatureSettings.length > 0 ? (
                                                    <Navigate to="/" replace />
                                                ) : null}
                                            </PrivateRoute>
                                        }
                                    />
                                </Routes>
                            </Suspense>
                        )}
                    </ContentWrapper>
                    {!isVerifyingToken && !isAuthenticating && visibleSidePanelModuleIndex !== SidePanelModulEnum.NONE && <SidePanelPopup />}
                </PageWrapper>
            </MaintenanceGuard>
        </AppWrapper>
    );
};

export default RouterContent;
